import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBarEntry } from '@nucleus-commerce/admin-kit/save-bar';
import type { Market, MarketPrice } from '@nucleus-commerce/api-client/admin';

import { marketKeys, useMarkets } from '@/features/markets';

import { storeCatalogApi } from '../api/store-catalog-api';
import {
  draftFromEntry,
  isMarketDraftDirty,
  type MarketPriceDraft,
} from '../schemas/market-price-rules';

import { storeCatalogKeys } from './store-catalog-keys';
import { marketPricesQueryOptions } from './use-market-prices';

/** A blank input on a saved fixed price removes it; the PUT itself only ever upserts. */
export function marketEditKind(
  draft: MarketPriceDraft,
  entry: MarketPrice | undefined,
): 'none' | 'set' | 'remove' {
  if (!draft.price) return entry ? 'remove' : 'none';
  return isMarketDraftDirty(draft, entry) ? 'set' : 'none';
}

export async function writeMarketEdit(
  storeId: string,
  marketId: string,
  variantId: string,
  draft: MarketPriceDraft,
  entry: MarketPrice | undefined,
): Promise<void> {
  const kind = marketEditKind(draft, entry);
  if (kind === 'remove') {
    await storeCatalogApi.removeMarketPrice(storeId, marketId, variantId);
  } else if (kind === 'set' && draft.price) {
    await storeCatalogApi.setMarketPrices(storeId, marketId, {
      items: [{ variantId, price: draft.price, compareAt: draft.compareAt }],
    });
  }
}

export interface VariantMarketDrafts {
  /** The store's markets other than the default one, whose price is the store price. */
  markets: readonly Market[];
  isPending: boolean;
  draftFor: (marketId: string, variantId: string) => MarketPriceDraft;
  isEdited: (marketId: string, variantId: string) => boolean;
  setDraft: (marketId: string, variantId: string, draft: MarketPriceDraft) => void;
  canWrite: boolean;
  saveError: unknown;
}

const keyOf = (marketId: string, variantId: string) => `${marketId}:${variantId}`;

/**
 * A store variant page's market price inputs as drafts, with their own save bar entry. The save
 * bar's leave guard refuses Previous, Next and the rail while anything is unsaved, so a person
 * saves or discards before moving to another variant. Keys carry the variant because this page
 * stays mounted across variants.
 */
export function useVariantMarketDrafts(
  storeId: string,
  storeProductId: string,
  productId: string,
): VariantMarketDrafts {
  const { can } = usePermissions();
  const canWrite = can('products.write');
  const queryClient = useQueryClient();
  const marketsQuery = useMarkets(storeId);
  const markets = useMemo(
    () => (marketsQuery.data ?? []).filter((market) => !market.isDefault),
    [marketsQuery.data],
  );
  const prices = useQueries({
    queries: markets.map((market) => ({
      ...marketPricesQueryOptions(storeId, market.id, productId),
      enabled: productId !== '',
    })),
  });
  const entries = new Map<string, MarketPrice>(
    markets.flatMap((market, index) =>
      (prices[index]?.data?.items ?? []).map(
        (entry) => [keyOf(market.id, entry.variantId), entry] as const,
      ),
    ),
  );
  const [drafts, setDrafts] = useState<
    Map<string, { marketId: string; variantId: string; draft: MarketPriceDraft }>
  >(new Map());

  const edits = [...drafts].filter(
    ([key, { draft }]) => marketEditKind(draft, entries.get(key)) !== 'none',
  );

  const save = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(
        edits.map(([key, { marketId, variantId, draft }]) =>
          writeMarketEdit(storeId, marketId, variantId, draft, entries.get(key)),
        ),
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [...storeCatalogKeys.all(storeId), 'market-prices'],
        }),
        queryClient.invalidateQueries({
          queryKey: storeCatalogKeys.pricing(storeId, storeProductId),
        }),
        queryClient.invalidateQueries({ queryKey: marketKeys.list(storeId) }),
      ]);
      setDrafts((current) => {
        const next = new Map(current);
        results.forEach((result, index) => {
          const key = edits[index]?.[0];
          if (key && result.status === 'fulfilled') next.delete(key);
        });
        return next;
      });
      const failed = results.find((result) => result.status === 'rejected');
      if (failed) {
        const reason = failed.reason as unknown;
        throw reason instanceof Error ? reason : new Error(String(reason));
      }
    },
    meta: { successMessage: 'storeCatalog:marketPrices.toasts.saved', silentError: true },
  });

  useSaveBarEntry({
    dirty: edits.length > 0,
    saving: save.isPending,
    onSave: () => save.mutateAsync(),
    onDiscard: () => setDrafts(new Map()),
    disabled: !canWrite,
  });

  return {
    markets,
    isPending: marketsQuery.isPending,
    draftFor: (marketId, variantId) => {
      const key = keyOf(marketId, variantId);
      return drafts.get(key)?.draft ?? draftFromEntry(entries.get(key));
    },
    isEdited: (marketId, variantId) => {
      const key = keyOf(marketId, variantId);
      const held = drafts.get(key);
      return held ? marketEditKind(held.draft, entries.get(key)) !== 'none' : false;
    },
    setDraft: (marketId, variantId, draft) =>
      setDrafts((current) =>
        new Map(current).set(keyOf(marketId, variantId), { marketId, variantId, draft }),
      ),
    canWrite,
    saveError: save.error,
  };
}
